import React from 'react';
import { getActivityStats } from '../../activities/activityTypes';
import {
  FIELD_KEYS,
  pick,
  isTruthy,
  DetailTopBar,
  DetailHero,
  WeatherPlaceholder,
} from './ActivityDetailPage';

// Ferrata-specific field keys (on top of the shared ones).
const FERRATA_KEYS = {
  desarrollo: ['desarrollo', 'length', 'longitud', 'dimension', 'recorrido'],
  tiempo: ['tiempo', 'time', 'duracion'],
  aprox: ['aproximacion', 'approach', 'aprox', 'acceso'],
  cruxSection: ['tramoMasDuro', 'crux', 'tramoDuro'],
  equipYear: ['equipadaAno', 'equipadaAnio', 'equipYear', 'anioEquipacion'],
  cable: ['cable', 'cableDiameter', 'cableMm'],
  familiar: ['familiar', 'aptaNinos', 'aptoNinos', 'conNinos', 'kids'],
  disipador: ['disipador', 'lanyard', 'dissipator'],
  aforo: ['aforo', 'capacity'],
};

// The Hüsler K-scale used for vías ferratas.
const HUSLER = [
  { k: 'K1', label: 'FÁCIL' },
  { k: 'K2', label: 'POCO DIF.' },
  { k: 'K3', label: 'ALGO DIF.' },
  { k: 'K4', label: 'DIFÍCIL' },
  { k: 'K5', label: 'MUY DIF.' },
  { k: 'K6', label: 'EXTREMO' },
];

/**
 * Vías ferratas detail page. Follows the v3 "05 · Vía ferrata" design: cota
 * belt, title with mini-stats, the Hüsler K-scale, description + safety flags,
 * and a hero photo beside the weather strip. Any datum absent from the activity
 * is omitted; the weather forecast is a placeholder.
 */
function FerrataDetail({ activity }) {
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const gpx = pick(values, FIELD_KEYS.gpx);

  const difficulty = getActivityStats(activity).find((s) => s.present);
  // K-level 1..6 parsed from a value like "K4" (or a bare number); NaN otherwise.
  const level = difficulty ? parseInt(String(difficulty.value).replace(/\D/g, ''), 10) : NaN;
  // Title-cased Hüsler label for the parsed level (e.g. "Difícil").
  const rawLabel = HUSLER[level - 1]?.label;
  const levelLabel = rawLabel ? rawLabel.charAt(0) + rawLabel.slice(1).toLowerCase() : null;

  const desarrollo = pick(values, FERRATA_KEYS.desarrollo);
  const tiempo = pick(values, FERRATA_KEYS.tiempo);
  const aprox = pick(values, FERRATA_KEYS.aprox);
  const crux = pick(values, FERRATA_KEYS.cruxSection);
  const equipYear = pick(values, FERRATA_KEYS.equipYear);
  const cable = pick(values, FERRATA_KEYS.cable);
  const familiar = pick(values, FERRATA_KEYS.familiar);
  const disipador = pick(values, FERRATA_KEYS.disipador);
  const status = pick(values, FIELD_KEYS.status);
  const aforo = pick(values, FERRATA_KEYS.aforo);

  const miniStats = [
    desarrollo && { key: 'des', label: 'DESARROLLO', value: desarrollo, unit: 'm' },
    tiempo && { key: 'time', label: 'TIEMPO', value: tiempo, unit: 'h' },
    aprox && { key: 'aprox', label: 'APROX.', value: aprox, unit: 'min' },
  ].filter(Boolean);

  const flags = [
    familiar && {
      key: 'fam',
      text: isTruthy(familiar) ? 'Apta con niños' : 'No apta con niños',
      color: isTruthy(familiar) ? 'var(--flik-status-open)' : 'var(--flik-text-faint)',
    },
    disipador && {
      key: 'dis',
      text: isTruthy(disipador) ? 'Disipador obligatorio' : String(disipador),
      color: 'var(--flik-text-faint)',
    },
    status && { key: 'st', text: String(status), color: 'var(--flik-status-open)' },
  ].filter(Boolean);

  const eyebrow = [
    equipYear && `EQUIPADA EN ${equipYear}`,
    cable && `CABLE DE ${String(cable).toUpperCase()}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const showCard = difficulty || description || flags.length > 0;

  return (
    <div className="Detail">
      <DetailTopBar
        activity={activity}
        rightCell={aforo && <span className="Detail__barCell Detail__barCell--gpx">{String(aforo).toUpperCase()}</span>}
      />

      {/* Title + mini-stats header (no hero on top for this type). */}
      <div className="Detail__ferrataHead">
        <div className="Detail__titleText">
          {eyebrow && <span className="Detail__eyebrow">{eyebrow}</span>}
          <h1 className="Detail__title Detail__title--onDark">{activity.name}</h1>
        </div>
        {miniStats.length > 0 && (
          <div className="Detail__miniStats">
            {miniStats.map(({ key, label, value, unit }) => (
              <div key={key} className="Detail__stat">
                <span className="Detail__statLabel">{label}</span>
                <span className="Detail__statVal">
                  {value} <em className="Detail__statUnit">{unit}</em>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="Detail__ferrataBody">
        {showCard && (
          <div className="Detail__ferrataCard flik-cut-xxl">
            {difficulty && (
              <>
                <div className="Detail__ferrataCardHead">
                  <span>DIFICULTAD · ESCALA HÜSLER</span>
                  {crux && <span>TRAMO MÁS DURO: {String(crux).toUpperCase()}</span>}
                </div>
                <div className="Detail__hscale">
                  {HUSLER.map((h, i) => (
                    <div key={h.k} className={`Detail__hcell${i + 1 === level ? ' is-on' : ''}`}>
                      <span className="Detail__hcellK">{h.k}</span>
                      <span className="Detail__hcellLabel">{h.label}</span>
                    </div>
                  ))}
                </div>

                {/* Compact difficulty card — mobile only (replaces the grid). */}
                <div className="Detail__hcompact">
                  <div className="Detail__hcompactTop">
                    <span className="Detail__hcompactK">{difficulty.value}</span>
                    <div className="Detail__hcompactInfo">
                      <span className="Detail__hcompactEyebrow">DIFICULTAD · HÜSLER</span>
                      {levelLabel && <span className="Detail__hcompactLabel">{levelLabel}</span>}
                      {crux && <span className="Detail__hcompactSub">{String(crux).toUpperCase()}</span>}
                    </div>
                  </div>
                  <div className="Detail__hbar" aria-hidden="true">
                    {HUSLER.map((h, i) => (
                      <span key={h.k} className={`Detail__hbarSeg${i + 1 === level ? ' is-on' : ''}`} />
                    ))}
                  </div>
                </div>
              </>
            )}

            {(description || flags.length > 0) && (
              <div className="Detail__ferrataCardBody">
                {description && (
                  <div className="Detail__ferrataDesc">
                    {String(description)
                      .split(/\n{2,}/)
                      .map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                  </div>
                )}
                {flags.length > 0 && (
                  <ul className="Detail__flags">
                    {flags.map((f) => (
                      <li key={f.key} className="Detail__flag">
                        <span className="Detail__flagDot" style={{ background: f.color }} aria-hidden="true" />
                        {f.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <div className="Detail__ferrataBottom">
          <DetailHero activity={activity} />
          <WeatherPlaceholder activity={activity} />
        </div>
      </div>

      {gpx && (
        <a className="Detail__gpxFooter" href={gpx} download>
          <span className="Detail__gpxFooterText">
            <span>SIGUIENTE PASO</span>
            <strong>DESCARGAR GPX</strong>
          </span>
          <span className="Detail__gpxFooterIcon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="butt">
              <path d="M12 4 L12 16 M6 11 L12 17 L18 11 M4 20 L20 20" />
            </svg>
          </span>
        </a>
      )}
    </div>
  );
}

export default FerrataDetail;
